import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { isBR } from "@/lib/market";
import { usePublicContactSettings } from "@/hooks/usePublicContactSettings";

interface FAQ {
  question: string;
  answer: string;
}

interface AggregateRating {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
}

interface ItemListItem {
  name: string;
  slug: string;
  image?: string;
}

interface VideoObject {
  url: string;
  thumbnailUrl: string;
  name: string;
  description?: string;
  uploadDate?: string;
  duration?: string;
}

interface HowToStep {
  title: string;
  description: string;
  image?: string;
}

interface HowTo {
  name: string;
  description: string;
  steps: HowToStep[];
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  /** Extra gallery images (enables Google's image carousel for products) */
  images?: string[];
  url?: string;
  type?: "website" | "article" | "product";
  // Product specific
  productName?: string;
  productPrice?: number;
  productCurrency?: string;
  productAvailability?: "InStock" | "OutOfStock" | "PreOrder";
  productBrand?: string;
  productCategory?: string;
  productRating?: AggregateRating;
  productSku?: string;
  productMpn?: string;
  productGtin?: string;
  productKeywords?: string;
  // Separate Open Graph (optional, falls back to title/description)
  ogTitle?: string;
  ogDescription?: string;
  // Article specific
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleKeywords?: string[];
  // Breadcrumb
  breadcrumbs?: { name: string; url: string }[];
  // FAQ Schema
  faqs?: FAQ[];
  noIndex?: boolean;
  // ItemList for category pages
  itemList?: {
    items: ItemListItem[];
    itemType: "equipment" | "consumable" | "article";
  };
  // VideoObject for products with video
  video?: VideoObject;
  // HowTo for operational guides
  howTo?: HowTo;
  // CollectionPage
  isCollectionPage?: boolean;
  collectionName?: string;
  // WebPage type
  pageType?: "WebPage" | "AboutPage" | "ContactPage" | "FAQPage" | "CollectionPage" | "ItemPage";
  // Event for articles about events
  event?: {
    name: string;
    startDate: string;
    endDate: string;
    location: string;
    locationAddress?: string;
    description?: string;
    url?: string;
  };
}

const SITE_NAME = "HEXATECH";
// On the Brazilian domain, canonical / og / JSON-LD URLs must point at Brazil,
// otherwise Google treats every BR page as a duplicate of the PT page.
const SITE_URL = isBR ? "https://hexatechbrasil.com.br" : "https://hexatech.pt";
const DEFAULT_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/qc57xtIpH9Soc5n5DHKovzbTebj2/social-images/social-1768755027417-logo%20novo.png";
const DEFAULT_DESCRIPTION = "Impressora DTF Portugal — distribuidor oficial HEXATECH. Equipamentos DTF com cabeças Epson i3200, tintas, filme e pó adesivo. Assistência técnica especializada e reparação de cabeças.";

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  images,
  url,
  type = "website",
  productName,
  productPrice,
  productCurrency = isBR ? "BRL" : "EUR",
  productAvailability = "InStock",
  productBrand = "HEXATECH",
  productCategory,
  productRating,
  productSku,
  productMpn,
  productGtin,
  productKeywords,
  ogTitle,
  ogDescription,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleKeywords,
  breadcrumbs,
  faqs,
  noIndex = false,
  itemList,
  video,
  howTo,
  isCollectionPage = false,
  collectionName,
  pageType,
  event,
}: SEOProps) {
  const { identity } = usePublicContactSettings();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const { pathname } = useLocation();
  // Drop the static index.html robots tag so the per-route one (e.g. noindex on 404) is the only one.
  useEffect(() => {
    document.querySelectorAll('meta[name="robots"]:not([data-rh])').forEach((el) => el.remove());
  }, []);
  // Clean canonical URL — fall back to the current route (never the homepage)
  const rawUrl = url || pathname || "/";
  const cleanUrl = rawUrl.split('?')[0].split('#')[0];
  const pathPart = cleanUrl === "/" ? "/" : cleanUrl.replace(/\/$/, "");
  const fullUrl = `${SITE_URL}${pathPart}`;
  const ptUrl = `https://hexatech.pt${pathPart}`;
  const brUrl = `https://hexatechbrasil.com.br${pathPart}`;

  const truncatedDescription = description.length > 160
    ? description.substring(0, 157) + "..." 
    : description;

  // Open Graph values (with fallbacks)
  const effectiveOgTitle = ogTitle || fullTitle;
  const effectiveOgDescription = ogDescription || truncatedDescription;

  // Absolute URL for og:image / twitter:image (crawlers require https:// URLs)
  const absoluteImage =
    image && (image.startsWith("http") ? image : `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`);

  // Gallery images for Google's product image carousel (absolute URLs, deduped)
  const schemaImages = Array.from(
    new Set(
      [image, ...(images || [])]
        .filter((src): src is string => Boolean(src))
        .map((src) => (src.startsWith("http") ? src : `${SITE_URL}${src.startsWith("/") ? "" : "/"}${src}`))
    )
  ).slice(0, 6);

  // Product structured data (JSON-LD) - Google requires offers, review, or aggregateRating
  const productJsonLd = type === "product" && productName ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    description: truncatedDescription,
    image: schemaImages.length > 1 ? schemaImages : image,
    url: fullUrl,
    ...(productSku ? { sku: productSku } : {}),
    ...(productMpn ? { mpn: productMpn } : {}),
    ...(productGtin ? { gtin13: productGtin } : {}),
    ...(productKeywords ? { keywords: productKeywords } : {}),
    brand: {
      "@type": "Brand",
      name: productBrand,
    },
    category: productCategory,
    // Always include offers - use price if available, otherwise use "Consultar" pattern
    offers: {
      "@type": "Offer",
      url: fullUrl,
      priceCurrency: productCurrency,
      ...(productPrice
        ? { price: productPrice }
        : { priceSpecification: { "@type": "PriceSpecification", priceCurrency: productCurrency, description: "Sob consulta" } }),
      availability: `https://schema.org/${productAvailability}`,
      seller: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: isBR ? "BR" : "PT",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 1,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 3,
            unitCode: "DAY",
          },
        },
      },
    },
    // Only expose aggregateRating when real review data exists
    ...(productRating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: productRating.ratingValue,
            reviewCount: productRating.reviewCount,
            bestRating: productRating.bestRating || 5,
          },
        }
      : {}),

  } : null;

  // Article structured data
  const articleJsonLd = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: truncatedDescription,
    image: image,
    datePublished: articlePublishedTime,
    dateModified: articleModifiedTime || articlePublishedTime,
    ...(articleSection ? { articleSection } : {}),
    ...(articleKeywords && articleKeywords.length > 0 ? { keywords: articleKeywords.join(", ") } : {}),
    author: {
      "@type": "Person",
      name: articleAuthor || SITE_NAME,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": fullUrl,
    },
  } : null;

  // Breadcrumb structured data
  const breadcrumbJsonLd = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  } : null;

  // FAQ structured data for rich snippets
  const faqJsonLd = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  } : null;

  // Organization structured data (for homepage)
  const organizationJsonLd = cleanUrl === "/" ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    ...(schemaImages.length > 0 ? { image: schemaImages } : {}),
    description: DEFAULT_DESCRIPTION,
    ...(isBR
      ? {
          legalName: "HEXATECH BRASIL LTDA",
          taxID: "69.481.076/0001-82",
          address: {
            "@type": "PostalAddress",
            streetAddress: "R. Johann Sachse, 539 — Badenfurt",
            addressLocality: "Blumenau",
            addressRegion: "SC",
            addressCountry: "BR",
          },
          areaServed: "BR",
        }
      : {
          email: identity.email,
          address: { "@type": "PostalAddress", streetAddress: identity.addressLine1, addressLocality: identity.city, postalCode: identity.postalCode, addressCountry: "PT" },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+351-963-920-495",
            contactType: "customer service",
            availableLanguage: ["Portuguese"],
          },
          areaServed: "PT",
        }),
  } : null;

  // ItemList structured data for category pages (carousel in Google)
  const itemListJsonLd = itemList && itemList.items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: collectionName || title,
    numberOfItems: itemList.items.length,
    itemListElement: itemList.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${SITE_URL}/${itemList.itemType === "equipment" ? "equipamento" : itemList.itemType === "consumable" ? "consumivel" : "blog"}/${item.slug}`,
      name: item.name,
      ...(item.image ? { image: item.image } : {}),
    })),
  } : null;

  // VideoObject structured data for products with video
  const videoJsonLd = video ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.name,
    description: video.description || truncatedDescription,
    thumbnailUrl: video.thumbnailUrl,
    contentUrl: video.url,
    uploadDate: video.uploadDate || new Date().toISOString(),
    ...(video.duration ? { duration: video.duration } : {}),
  } : null;

  // HowTo structured data for operational guides
  const howToJsonLd = howTo ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howTo.name,
    description: howTo.description,
    step: howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.title,
      text: step.description,
      ...(step.image ? { image: step.image } : {}),
    })),
  } : null;

  // CollectionPage structured data for category pages
  const collectionPageJsonLd = isCollectionPage ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: collectionName || title,
    description: truncatedDescription,
    url: fullUrl,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
  } : null;

  // WebPage structured data for static pages
  const webPageJsonLd = pageType && !isCollectionPage ? {
    "@context": "https://schema.org",
    "@type": pageType,
    name: title,
    description: truncatedDescription,
    url: fullUrl,
    ...(schemaImages.length > 0 ? {
      image: schemaImages,
      primaryImageOfPage: { "@type": "ImageObject", url: schemaImages[0] },
    } : {}),
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
  } : null;

  // Event structured data for articles about events
  const eventJsonLd = event ? {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.name,
    description: event.description || truncatedDescription,
    startDate: event.startDate,
    endDate: event.endDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: event.location,
      address: {
        "@type": "PostalAddress",
        streetAddress: event.locationAddress || "",
        addressLocality: event.location,
      },
    },
    organizer: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    ...(event.url ? { url: event.url } : {}),
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      <link rel="canonical" href={fullUrl} />
      {!noIndex && <link rel="alternate" hrefLang="pt-PT" href={ptUrl} />}
      {!noIndex && <link rel="alternate" hrefLang="pt-BR" href={brUrl} />}
      {!noIndex && <link rel="alternate" hrefLang="x-default" href={ptUrl} />}
      
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:title" content={effectiveOgTitle} />
      <meta property="og:description" content={effectiveOgDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type === "product" ? "product" : type === "article" ? "article" : "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={isBR ? "pt_BR" : "pt_PT"} />
      <meta property="og:locale:alternate" content={isBR ? "pt_PT" : "pt_BR"} />

      {/* Product specific OG tags */}
      {type === "product" && productPrice && (
        <>
          <meta property="product:price:amount" content={productPrice.toString()} />
          <meta property="product:price:currency" content={productCurrency} />
          <meta property="product:availability" content={productAvailability.toLowerCase()} />
          <meta property="product:brand" content={productBrand} />
          {productCategory && <meta property="product:category" content={productCategory} />}
        </>
      )}

      {/* Article specific OG tags */}
      {type === "article" && (
        <>
          {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
          {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
          {articleAuthor && <meta property="article:author" content={articleAuthor} />}
          {articleSection && <meta property="article:section" content={articleSection} />}
          {articleKeywords && articleKeywords.map((keyword, i) => (
            <meta key={i} property="article:tag" content={keyword} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:site" content="@hexatech_pt" />

      {/* Structured Data */}
      {productJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd)}
        </script>
      )}
      {articleJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}
      {faqJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      )}
      {organizationJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
      )}
      {itemListJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(itemListJsonLd)}
        </script>
      )}
      {videoJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(videoJsonLd)}
        </script>
      )}
      {howToJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(howToJsonLd)}
        </script>
      )}
      {collectionPageJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(collectionPageJsonLd)}
        </script>
      )}
      {webPageJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(webPageJsonLd)}
        </script>
      )}
      {eventJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(eventJsonLd)}
        </script>
      )}
    </Helmet>
  );
}